import { useEffect, useMemo, useState } from "react";
import { useAgents, useCommissions, useParts, useRegles, useUsines, lireParametre } from "@/lib/donnees";
import { useNiveau, useSession } from "@/lib/session";
import { Kpi, Kpis, Section, Segment, Tag } from "@/ui/primitives";
import { Table } from "@/ui/Table";
import type { Colonne } from "@/ui/Table";
import { euro, euroCourt } from "@/lib/format";
import { ETATS_COMMISSION as ETATS, voitTotal } from "@/domain/commissions";
import { MODE_LONG, NOM_MODE, NOM_PART, valeurBareme } from "./libelles";
import type { Commission, EtatCommission, Transparence } from "@/types/metier";

export function Commissions() {
  const { profil } = useSession();
  const niveau = useNiveau();
  const { donnees: commissions, chargement, erreur } = useCommissions();
  const { donnees: regles } = useRegles();
  const { donnees: parts } = useParts();
  const { donnees: usines } = useUsines();
  const { donnees: agents } = useAgents();
  const [transparence, setTransparence] = useState<Transparence>("niveau");
  const [etat, setEtat] = useState<EtatCommission | null>(null);

  useEffect(() => {
    void lireParametre<Transparence>("transparence", "niveau").then(setTransparence);
  }, []);

  const tout = voitTotal(niveau, transparence);
  const nomUsine = useMemo(() => new Map(usines.map((u) => [u.id, u.nom] as const)), [usines]);
  const nomAgent = useMemo(() => new Map(agents.map((a) => [a.id, a.nom] as const)), [agents]);

  const miennes = useMemo(
    () => (niveau === "admin" ? commissions : commissions.filter((c) => c.agent_id === profil?.id)),
    [commissions, niveau, profil?.id],
  );
  const lignes = etat ? miennes.filter((c) => c.etat === etat) : miennes;

  const vu = (c: Commission) => Number(tout ? c.montant : c.agent_montant);
  const somme = (etats: EtatCommission[]) =>
    miennes.filter((c) => etats.includes(c.etat)).reduce((s, c) => s + vu(c), 0);

  const colonnes: (Colonne<Commission> | false)[] = [
    {
      cle: "reference",
      titre: "Commission",
      type: "ref",
      filtre: "texte",
      sous: (c) => ETATS.find((e) => e.cle === c.etat)?.nom.toLowerCase() ?? c.etat,
    },
    /* La colonne ne dit quelque chose que si plusieurs agents sont à
       l'écran : un agent ne voit que ses propres commissions. */
    niveau === "admin" && {
      cle: "agent_id",
      titre: "Agent",
      mobile: false,
      filtre: "select",
      texte: (c) => nomAgent.get(c.agent_id) ?? "—",
    },
    {
      cle: "usine_id",
      titre: "Usine",
      mobile: false,
      filtre: "select",
      texte: (c) => nomUsine.get(c.usine_id) ?? "—",
    },
    { cle: "base", titre: "Base HT", type: "euro", total: true, mobile: false,
      nombre: (c) => Number(c.base) },
    tout && {
      cle: "montant",
      titre: "Commission totale",
      court: "Total",
      type: "euro",
      total: true,
      nombre: (c) => Number(c.montant),
      sous: (c) => NOM_MODE[c.mode] ?? c.mode,
    },
    {
      cle: "agent_montant",
      titre: tout ? "Part agent" : "Commission",
      court: tout ? "Agent" : "Montant",
      type: "euro",
      total: true,
      nombre: (c) => Number(c.agent_montant),
    },
    tout && {
      cle: "omnix_montant",
      titre: "Reste à Omnix",
      court: "Omnix",
      type: "euro",
      total: true,
      mobile: false,
      nombre: (c) => Number(c.omnix_montant),
    },
    {
      cle: "etat",
      titre: "État",
      filtre: "select",
      mobile: false,
      texte: (c) => ETATS.find((e) => e.cle === c.etat)?.nom ?? c.etat,
      rendu: (c) => {
        const e = ETATS.find((x) => x.cle === c.etat);
        return (
          <Tag ton={e?.ton} dot>
            {e?.nom ?? c.etat}
          </Tag>
        );
      },
    },
  ];

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  return (
    <>
      <div className="filterbar">
        <Segment
          label="État"
          valeur={etat}
          onChange={setEtat}
          options={[
            { cle: null, nom: "Toutes" },
            ...ETATS.map((e) => ({
              cle: e.cle,
              nom: e.nom,
              n: miennes.filter((c) => c.etat === e.cle).length,
            })),
          ]}
        />
      </div>

      <div className="pad">
        <Kpis>
          <Kpi
            k="Engagé"
            v={euroCourt(somme(["brouillon", "confirmee"]))}
            n="commande passée, pas encaissée"
          />
          <Kpi
            k="À facturer"
            v={euroCourt(somme(["a_facturer"]))}
            n={tout ? "facture Omnix à émettre" : "à refacturer à Omnix"}
          />
          <Kpi
            k="Réglé"
            v={euroCourt(somme(["payee"]))}
            n={`${euroCourt(somme(["facturee"]))} facturés en attente`}
          />
        </Kpis>

        <Section titre="Commissions" note={chargement ? "chargement…" : `${lignes.length} lignes`}>
          <Table lignes={lignes} cle={(c) => c.id} colonnes={colonnes} chercher="Commission ou vente" />
        </Section>

        {niveau === "admin" ? (
          <>
            <Section
              titre="Barème usines"
              note="ce qu'Omnix prend à l'usine — la règle la plus précise l'emporte"
            >
              <Table
                nu
                lignes={regles}
                cle={(r) => r.id}
                colonnes={[
                  {
                    cle: "usine_id",
                    titre: "Usine",
                    filtre: "select",
                    texte: (r) => nomUsine.get(r.usine_id) ?? "—",
                    sous: (r) => r.gamme ?? "tout le catalogue",
                  },
                  { cle: "gamme", titre: "Gamme visée", court: "Gamme", mobile: false,
                    texte: (r) => r.gamme ?? "Tout le catalogue" },
                  { cle: "mode", titre: "Mode", mobile: false, texte: (r) => MODE_LONG[r.mode] },
                  {
                    cle: "valeur",
                    titre: "Barème",
                    type: "num",
                    texte: (r) => valeurBareme(r.mode, Number(r.valeur)),
                    rendu: (r) => <span className="mono">{valeurBareme(r.mode, Number(r.valeur))}</span>,
                  },
                ]}
              />
            </Section>

            <Section titre="Part des agents" note="déduite de la commission, jamais ajoutée">
              <Table
                nu
                lignes={parts}
                cle={(p) => p.id}
                colonnes={[
                  {
                    cle: "agent_id",
                    titre: "Agent",
                    filtre: "select",
                    texte: (p) => nomAgent.get(p.agent_id) ?? "—",
                    sous: (p) => (p.usine_id ? (nomUsine.get(p.usine_id) ?? "—") : "toutes usines"),
                  },
                  { cle: "mode", titre: "Mode", mobile: false, texte: (p) => NOM_PART[p.mode] },
                  {
                    cle: "valeur",
                    titre: "Part",
                    type: "num",
                    texte: (p) => valeurBareme(p.mode, Number(p.valeur)),
                    rendu: (p) => <span className="mono">{valeurBareme(p.mode, Number(p.valeur))}</span>,
                  },
                  {
                    cle: "du",
                    titre: "Dû sur les commissions en cours",
                    court: "Dû",
                    type: "euro",
                    total: true,
                    mobile: false,
                    nombre: (p) =>
                      commissions
                        .filter((c) => c.agent_id === p.agent_id && (!p.usine_id || c.usine_id === p.usine_id))
                        .reduce((s, c) => s + Number(c.agent_montant), 0),
                  },
                ]}
              />
            </Section>
          </>
        ) : null}

        {tout ? null : (
          <div className="banner">
            Vous voyez votre part de commission. Le barème pris à l'usine relève de la direction.
          </div>
        )}
        <div className="vide-ligne">
          Total des parts agents : <b className="mono">{euro(
            miennes.reduce((s, c) => s + Number(c.agent_montant), 0),
          )}</b>
        </div>
      </div>
    </>
  );
}
